import React from 'react';

import { useIntl } from 'ha/i18n';
import { CurrencyFormat } from '@ha/intl';
import { Grid, Link, Typography, units } from '@hbf/dsl';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import { makeStyles } from 'tss-react/mui';

import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';
import { UtilityBillsLabelByValue } from 'ha/models/ListingUtilityBills/translations';

import { UtilityBills } from 'ha/models/Listing/types';

interface Props {
  currency: string;
  minPrice: number;
  billsType: UtilityBills;
  onBillsClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const useStyles = makeStyles()(theme => ({
  billsContainer: {
    fontSize: units.rem(0.75),
    color: theme.palette.grey[600],

    [theme.breakpoints.down('md')]: {
      top: units.rem(0.188),
    },
  },
  fromLabel: {
    color: theme.palette.secondary.dark,
    fontFamily: sharpSansBold.fontFamily,
    fontSize: units.rem(0.75),
    textTransform: 'uppercase',
    marginRight: units.rem(0.25),
    fontWeight: 'bold',
  },
  priceLabel: {
    color: theme.palette.secondary.dark,
    fontFamily: sharpSansBold.fontFamily,
    fontSize: units.rem(1.25),
  },
}));

const FromPrice: React.FC<Props> = ({
  billsType,
  currency,
  minPrice,
  onBillsClick,
}) => {
  const { formatCurrency, T } = useIntl();
  const { classes } = useStyles();

  const currencyFormat =
    currency === 'EUR'
      ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
      : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING;

  const subtitle = T('Monthly (%s)', T(UtilityBillsLabelByValue[billsType]));

  return (
    <Grid container direction="column" data-test-locator="Listing/FromPrice">
      <Grid item>
        <Grid container>
          <Grid item data-test-locator="Listing/from_price/test">
            <span className={classes.fromLabel}>{T('from_price')}</span>
          </Grid>
          <Grid item>
            <Typography.H3 fontWeight="bold" className={classes.priceLabel}>
              {formatCurrency(minPrice, currency, currencyFormat)}
            </Typography.H3>
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        {onBillsClick ? (
          <Track name="Clicked Bills Anchor link" type="link">
            <Link
              to=""
              onClick={onBillsClick}
              data-test-locator="Listing/FromPrice/Subtitle/Link"
            >
              <Typography.Caption
                data-test-locator="Listing/Price/Subtitle"
                color="muted"
              >
                {subtitle}
              </Typography.Caption>
            </Link>
          </Track>
        ) : (
          <Typography.Caption className={classes.billsContainer}>
            {subtitle}
          </Typography.Caption>
        )}
      </Grid>
    </Grid>
  );
};

export { FromPrice };
